import React from "react";
import { ArrowRight, Plus, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { IntegrationsCardProps } from "./IntegrationsCardInterface";

const IntegrationsCard: React.FC<IntegrationsCardProps> = ({
  name,
  logoSrc,
  isActive,
  isConnected,
  onConnect,
  onCancel,
  loading,
  type,
}) => {
  const { language } = useAuth();

  const objectFitClass = type === "stripe" ? "object-cover" : "object-contain";

  return (
    <div className="group relative bg-white rounded-[24px] border border-gray-100 px-7 py-5 flex flex-col items-center justify-between transition-all duration-300 hover:shadow-[0_8px_30px_rgba(67,104,229,0.12)] hover:-translate-y-1 h-[220px] w-full max-w-[240px] mx-auto overflow-hidden">
      {/* Background Hover Effect */}
      <div
        className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent ${
          isConnected ? "via-red-500" : "via-fluxi"
        } to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
      />

      {/* Status Badge */}
      <div className="w-full flex justify-center relative z-10">
        <div
          className={`flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[10px] font-bold tracking-wide border transition-colors ${
            isActive
              ? "bg-[#ecfdf5] text-[#059669] border-[#a7f3d0]"
              : "bg-gray-50 text-gray-400 border-gray-200"
          }`}
        >
          <div
            className={`w-1.5 h-1.5 rounded-full ${
              isActive ? "bg-[#10b981]" : "bg-gray-300"
            }`}
          />
          {isActive
            ? dictionary["active"][language]
            : dictionary["inactive"][language]}
        </div>
      </div>

      {/* Logo Container */}
      <div className="flex-1 flex flex-col items-center justify-center w-full relative z-10">
        <div className="p-2 group-hover:scale-105 transition-transform duration-300 flex items-center justify-center">
          <div className="h-14 w-auto flex items-center justify-center">
            <img
              src={logoSrc as string}
              alt={name}
              className={`max-h-14 w-auto ${objectFitClass}`}
            />
          </div>
        </div>
      </div>

      {/* Action Button */}
      {isConnected ? (
        <button
          onClick={onCancel}
          className="relative z-10 w-full flex items-center justify-center gap-2 h-[36px] rounded-[10px] text-[12px] font-semibold transition-all duration-200 border active:scale-[0.98] border-gray-200 text-gray-500 bg-gray-50 hover:bg-red-500 hover:text-white hover:shadow-lg hover:shadow-red-200 hover:border-red-500"
        >
          <span>
            {loading
              ? dictionary?.["loading"]?.[language] || "..."
              : dictionary["cancel"][language]}
          </span>
          <X className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          onClick={onConnect}
          className="relative z-10 w-full flex items-center justify-center gap-2 h-[36px] rounded-[10px] text-[12px] font-semibold transition-all duration-200 border active:scale-[0.98] border-fluxi text-fluxi hover:bg-fluxi hover:text-white hover:shadow-lg hover:shadow-blue-200"
        >
          <span>
            {loading
              ? dictionary?.["loading"]?.[language] || "..."
              : dictionary["connect"][language]}
          </span>
          {loading ? (
            <ArrowRight className="w-3.5 h-3.5" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
        </button>
      )}
    </div>
  );
};

export default IntegrationsCard;
